import axios from 'axios';
import { API } from '../../backend';
import authHeader from '../auth-header';

// API Endpoints
export const MAPPING_SAVE_API = `${API}company/create-or-check-ga-upload/`;
export const MAPPING_LIST_API = `${API}company/list-ga-uploads/`;
export const MAPPING_UPDATE_API = `${API}company/update-ga-upload/`;
export const PARTY_IMPORT_API = `${API}company/party-import/`;
export const PARTY_ADDRESS_IMPORT_API = `${API}company/partyaddress-import/`;
export const PARTY_CONTACT_IMPORT_API = `${API}company/partycontact-import/`;
export const CONTRACT_IMPORT_API = `${API}company/contracts-import/`;
export const CONTRACT_PAYMENT_IMPORT_API = `${API}company/contractpaymentschedule-import/`;
export const CONTRACT_ASSETS_IMPORT_API = `${API}company/contractassets-import/`;
export const ADDITIONAL_PARTIES_IMPORT_API = `${API}company/additionalparties-import/`;
export const INVOICE_IMPORT_API = `${API}company/invoice-import/`;
export const RECEIPT_IMPORT_API = `${API}company/receipt-import/`;
export const TAXRATE_IMPORT_API = `${API}company/taxrates-import/`;
export const INVENTORY_IMPORT_API = `${API}company/inventory-import/`;
export const ACH_ACCOUNTS_IMPORT_API = `${API}company/achaccounts-import/`;
export const TOKEN_REFRESH_API = `${API}token/refresh/`;

// Function to refresh the token
const refreshToken = async () => {
  try {
    const refresh = localStorage.getItem('refresh_token');
    if (!refresh) {
      throw new Error('No refresh token available');
    }
    const response = await axios.post(TOKEN_REFRESH_API, { refresh });
    const { access } = response.data;
    localStorage.setItem('access_token', access);
    return access;
  } catch (error) {
    console.error('Token refresh failed:', error);
    throw error;
  }
};

// List all mappings for the company
const listMappings = async () => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      ...authHeader(),
    };
    if (!headers.Authorization) {
      throw new Error('Authentication token is missing');
    }
    console.log('Fetching mappings list');
    const response = await axios.get(MAPPING_LIST_API, { headers });
    console.log('List mappings response:', response.data);
    return response.data;
  } catch (error) {
    if (error.response?.data?.code === 'token_not_valid') {
      try {
        await refreshToken();
        const headers = {
          'Content-Type': 'application/json',
          ...authHeader(),
        };
        const response = await axios.get(MAPPING_LIST_API, { headers });
        return response.data;
      } catch (refreshError) {
        console.error('Refresh token failed:', refreshError);
        throw refreshError;
      }
    }
    console.error('List mappings error:', error);
    throw error;
  }
};

// Save mapping with table display name
const saveMapping = async (payload) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      ...authHeader(),
    };
    if (!headers.Authorization) {
      throw new Error('Authentication token is missing');
    }
    console.log('Saving mapping with payload:', payload);
    const response = await axios.post(MAPPING_SAVE_API, payload, { headers });
    console.log('Save mapping response:', response.data);
    return response.data;
  } catch (error) {
    if (error.response?.data?.code === 'token_not_valid') {
      try {
        await refreshToken();
        const headers = {
          'Content-Type': 'application/json',
          ...authHeader(),
        };
        const response = await axios.post(MAPPING_SAVE_API, payload, { headers });
        return response.data;
      } catch (refreshError) {
        console.error('Refresh token failed:', refreshError);
        throw refreshError;
      }
    }
    console.error('Save mapping error:', error);
    throw error;
  }
};

// Update mapping by ID
const updateMapping = async (id, payload) => {
  try {
    const headers = {
      'Content-Type': 'application/json',
      ...authHeader(),
    };
    if (!headers.Authorization) {
      throw new Error('Authentication token is missing');
    }
    console.log(`Updating mapping with ID ${id} and payload:`, payload);
    const response = await axios.put(`${MAPPING_UPDATE_API}${id}/`, payload, { headers });
    console.log('Update mapping response:', response.data);
    return response.data;
  } catch (error) {
    if (error.response?.data?.code === 'token_not_valid') {
      try {
        await refreshToken();
        const headers = {
          'Content-Type': 'application/json',
          ...authHeader(),
        };
        const response = await axios.put(`${MAPPING_UPDATE_API}${id}/`, payload, { headers });
        return response.data;
      } catch (refreshError) {
        console.error('Refresh token failed:', refreshError);
        throw refreshError;
      }
    }
    console.error('Update mapping error:', error);
    throw error;
  }
};

// Generic import function with table display name
const importCsv = async (endpoint, formData) => {
  try {
    const headers = {
      'Content-Type': 'multipart/form-data',
      ...authHeader(),
    };
    if (!headers.Authorization) {
      throw new Error('Authentication token is missing');
    }
    console.log('Sending FormData to', endpoint, ':', Array.from(formData.entries()));
    const response = await axios.post(endpoint, formData, { headers });
    console.log('Import response from', endpoint, ':', response.data);
    return response.data;
  } catch (error) {
    if (error.response?.data?.code === 'token_not_valid') {
      try {
        await refreshToken();
        const headers = {
          'Content-Type': 'multipart/form-data',
          ...authHeader(),
        };
        console.log('Retrying with refreshed token, FormData:', Array.from(formData.entries()));
        const response = await axios.post(endpoint, formData, { headers });
        return response.data;
      } catch (refreshError) {
        console.error('Refresh token failed:', refreshError);
        return { success: false, error: refreshError.response?.data?.error || 'Authentication failed' };
      }
    }
    const errorResponse = error.response?.data || { error: error.message };
    console.error('Import error from', endpoint, ':', errorResponse);
    return { success: false, error: errorResponse.error || 'Failed to import data' };
  }
};

// Import functions for each table
const partyImport = (csvFile) => importCsv(PARTY_IMPORT_API, csvFile);
const partyAddressImport = (csvFile) => importCsv(PARTY_ADDRESS_IMPORT_API, csvFile);
const partyContactImport = (csvFile) => importCsv(PARTY_CONTACT_IMPORT_API, csvFile);
const contractImport = (csvFile) => importCsv(CONTRACT_IMPORT_API, csvFile);
const contractPaymentImport = (csvFile) => importCsv(CONTRACT_PAYMENT_IMPORT_API, csvFile);
const contractAssetsImport = (csvFile) => importCsv(CONTRACT_ASSETS_IMPORT_API, csvFile);
const additionalPartiesImport = (csvFile) => importCsv(ADDITIONAL_PARTIES_IMPORT_API, csvFile);
const invoiceImport = (csvFile) => importCsv(INVOICE_IMPORT_API, csvFile);
const receiptImport = (csvFile) => importCsv(RECEIPT_IMPORT_API, csvFile);
const taxRateImport = (csvFile) => importCsv(TAXRATE_IMPORT_API, csvFile);
const inventoryImport = (csvFile) => importCsv(INVENTORY_IMPORT_API, csvFile);
const achAccountsImport = (csvFile) => importCsv(ACH_ACCOUNTS_IMPORT_API, csvFile);

const JsonServices = {
  listMappings,
  saveMapping,
  updateMapping,
  partyImport,
  partyAddressImport,
  partyContactImport,
  contractImport,
  contractPaymentImport,
  contractAssetsImport,
  additionalPartiesImport,
  invoiceImport,
  receiptImport,
  taxRateImport,
  inventoryImport,
  achAccountsImport,
};

export default JsonServices;